import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import './styles/App.css';
import AuthPage from './pages/AuthPage';
import DashboardPage from './pages/DashboardPage';
import TopicPage from './pages/TopicPage';
import AdminPanel from './pages/AdminPanel';
import ApiAccessPage from './pages/ApiAccessPage';
import { IconMoon, IconSun } from './components/Icons';
import FlagImg from './components/FlagImg';
import Footer from './components/Footer';

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: '2rem', maxWidth: 480, margin: '4rem auto', fontFamily: 'sans-serif' }}>
          <h2 style={{ color: '#f4212e', marginBottom: '0.75rem' }}>Something went wrong</h2>
          <p style={{ color: '#536471', marginBottom: '1rem', fontSize: '0.875rem', lineHeight: 1.6 }}>
            The app failed to load. Please try refreshing the page.
          </p>
          <pre style={{ background: '#f7f9fb', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.75rem', fontSize: '0.75rem', overflowX: 'auto', color: '#0f1419', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {this.state.error.toString()}
          </pre>
          <button onClick={() => window.location.reload()} style={{ marginTop: '1rem', background: '#1d9bf0', color: '#fff', border: 'none', borderRadius: 9999, padding: '0.6rem 1.25rem', fontWeight: 700, cursor: 'pointer' }}>
            Refresh
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export const ThemeContext = createContext({ theme: 'light', toggle: () => {} });
export const useTheme = () => useContext(ThemeContext);

function getCountryFlag(countryCode) {
  if (!countryCode || countryCode.length !== 2) return '';
  const codePoints = countryCode.toUpperCase().split('').map(c => 127397 + c.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

function AdminLoginForm({ onAdminLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_BASE}/auth/admin/login`, { username, password });
      onAdminLogin(data.token, data.username, data.role);
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 360, margin: '4rem auto', padding: '2rem', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12 }}>
      <h2 style={{ marginBottom: '1.5rem' }}>Admin Login</h2>
      {error && <p style={{ color: '#dc2626', marginBottom: '1rem' }}>{error}</p>}
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <input value={username} onChange={e => setUsername(e.target.value)} placeholder="Email or username" required style={{ padding: '0.6rem 0.75rem', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input, var(--bg-card))', color: 'var(--text-primary)' }} />
        <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" required style={{ padding: '0.6rem 0.75rem', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input, var(--bg-card))', color: 'var(--text-primary)' }} />
        <button type="submit" disabled={loading} style={{ padding: '0.65rem', borderRadius: 8, background: '#4f46e5', color: '#fff', border: 'none', fontWeight: 700, cursor: 'pointer' }}>
          {loading ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}

function App() {
  const [user, setUser]               = useState(null);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [topics, setTopics]           = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [theme, setTheme]             = useState(() => localStorage.getItem('protesteTheme') || 'light');
  const [adminToken, setAdminToken]   = useState(() => localStorage.getItem('protesteAdminToken') || null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [verifiedBanner, setVerifiedBanner] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    const v = p.get('verified');
    if (v) window.history.replaceState({}, '', '/');
    return v; // 'true' | 'error' | null
  });

  const [resetToken] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    const t = p.get('reset_token');
    if (t) window.history.replaceState({}, '', '/');
    return t || null;
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('protesteTheme', theme);
  }, [theme]);

  // Android back button: push a history entry so the hardware back button
  // navigates within the app rather than exiting it.
  useEffect(() => {
    window.history.pushState({ page: currentPage }, '', window.location.pathname);
    const onPop = () => {
      if (currentPage === 'topic') {
        setCurrentPage('dashboard');
        setSelectedTopic(null);
      } else if (currentPage !== 'dashboard') {
        setCurrentPage('dashboard');
      }
      // Re-push so the next back press is also caught
      window.history.pushState({ page: 'dashboard' }, '', window.location.pathname);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [currentPage]);

  const toggleTheme = () => setTheme(t => t === 'light' ? 'dark' : 'light');

  // Restore user session and JWT on mount
  useEffect(() => {
    const storedUser  = localStorage.getItem('protesteUser');
    const storedToken = localStorage.getItem('protesteToken');
    if (storedUser && storedToken) {
      try {
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        setUser(JSON.parse(storedUser));
        if (!localStorage.getItem('protesteOnboarded')) setShowOnboarding(true);
      } catch {}
    } else if (storedUser) {
      // Legacy session without token — force re-login
      localStorage.removeItem('protesteUser');
    }
  }, []);

  // Always fetch topics on mount and every 30s (feed is public)
  useEffect(() => {
    fetchTopics();
    const id = setInterval(fetchTopics, 30000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTopics = async () => {
    try {
      const response = await axios.get(`${API_BASE}/topics`);
      setTopics(response.data);
    } catch (error) {
      console.error('Error loading topics:', error);
    }
  };

  const _storeSession = (userData, token) => {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('protesteToken', token);
    localStorage.setItem('protesteUser', JSON.stringify(userData));
    setUser(userData);
    if (!localStorage.getItem('protesteOnboarded')) setShowOnboarding(true);
  };

  const handleRegister = async (email, password, countryOrigin, countryResidence) => {
    const response = await axios.post(`${API_BASE}/auth/register`, {
      email,
      password,
      country_origin: countryOrigin,
      country_residence: countryResidence,
    });
    const newUser = response.data;
    _storeSession(newUser, newUser.token);
    setCurrentPage('dashboard');
  };

  const handleLogin = async (email, password) => {
    const response = await axios.post(`${API_BASE}/auth/login`, { email, password });
    const loggedInUser = response.data;
    _storeSession(loggedInUser, loggedInUser.token);
    setCurrentPage('dashboard');
  };

  const handleAdminLogin = (token, username, role) => {
    localStorage.setItem('protesteAdminToken', token);
    setAdminToken(token);
  };

  const handleLogout = () => {
    localStorage.removeItem('protesteUser');
    localStorage.removeItem('protesteToken');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setCurrentPage('dashboard');
    setSelectedTopic(null);
  };

  const dismissOnboarding = () => {
    localStorage.setItem('protesteOnboarded', '1');
    setShowOnboarding(false);
  };

  const handleSelectTopic = (topic) => {
    setSelectedTopic(topic);
    setCurrentPage('topic');
  };

  const handleAdminLogout = () => {
    localStorage.removeItem('protesteAdminToken');
    setAdminToken(null);
    setCurrentPage('dashboard');
  };

  // Admin is logged in — show full app with Admin tab in nav
  if (adminToken) {
    const adminPage = currentPage === 'admin' || currentPage === 'dashboard' || currentPage === 'topic'
      ? currentPage : 'dashboard';

    return (
      <ThemeContext.Provider value={{ theme, toggle: toggleTheme }}>
        <div className="app">
          <nav className="navbar">
            <div className="logo">
              <img src="/favicon.svg" alt="Proteste" className="logo-mark" />
              <div className="logo-copy">
                <span className="logo-title">Proteste</span>
                <span className="logo-tagline">The voice of the people, visualized</span>
              </div>
            </div>
            <div className="nav-links">
              <button onClick={() => setCurrentPage('dashboard')} className={adminPage === 'dashboard' || adminPage === 'topic' ? 'active' : ''}>
                Feed
              </button>
              <button onClick={() => setCurrentPage('admin')} className={adminPage === 'admin' ? 'active' : ''}>
                Admin
              </button>
            </div>
            <div className="user-info">
              <button className="theme-toggle" onClick={toggleTheme} title="Toggle dark/light mode">
                {theme === 'light' ? <IconMoon /> : <IconSun />}
              </button>
              <button className="logout-btn" onClick={handleAdminLogout}>Exit Admin</button>
            </div>
          </nav>
          <main className="main-content">
            {adminPage === 'admin' && (
              <AdminPanel onCreateTopic={fetchTopics} adminToken={adminToken} />
            )}
            {adminPage === 'dashboard' && (
              <DashboardPage
                user={user}
                topics={topics}
                onSelectTopic={(t) => { setSelectedTopic(t); setCurrentPage('topic'); }}
                onRefreshTopics={fetchTopics}
                adminMode
              />
            )}
            {adminPage === 'topic' && selectedTopic && (
              <TopicPage
                user={user}
                topic={selectedTopic}
                onBack={() => setCurrentPage('dashboard')}
                onSynthesize={fetchTopics}
                adminMode
              />
            )}
          </main>
          <Footer />
        </div>
      </ThemeContext.Provider>
    );
  }

  return (
    <ThemeContext.Provider value={{ theme, toggle: toggleTheme }}>
      <div className="app">
        <nav className="navbar">
          <div className="logo">
            <img src="/favicon.svg" alt="Proteste" className="logo-mark" />
            <div className="logo-copy">
              <span className="logo-title">Proteste</span>
              <span className="logo-tagline">The voice of the people, visualized</span>
            </div>
          </div>

          <div className="nav-links">
            <button
              onClick={() => setCurrentPage('dashboard')}
              className={currentPage === 'dashboard' || currentPage === 'topic' ? 'active' : ''}
            >
              Feed
            </button>
            <button
              onClick={() => setCurrentPage('api-access')}
              className={currentPage === 'api-access' ? 'active' : ''}
            >
              API Access
            </button>
          </div>

          <div className="user-info">
            {user ? (
              <>
                <div className="user-anon-id">
                  <span className="anon-label">Anonymous ID</span>
                  <span className="anon-value">{user.user_id}</span>
                </div>
                <span className="flag-badge" title="Your country of residence">
                  <FlagImg code={user.country_residence} size={18} />
                </span>
                <button className="theme-toggle" onClick={toggleTheme} title="Toggle dark/light mode">
                  {theme === 'light' ? <IconMoon /> : <IconSun />}
                </button>
                <button className="logout-btn" onClick={handleLogout}>Sign out</button>
              </>
            ) : (
              <>
                <button className="theme-toggle" onClick={toggleTheme} title="Toggle dark/light mode">
                  {theme === 'light' ? <IconMoon /> : <IconSun />}
                </button>
                <button className="logout-btn" onClick={() => setCurrentPage('login')}>Sign in</button>
              </>
            )}
          </div>
        </nav>

        <main className="main-content">
          {verifiedBanner && (
            <div className="onboarding-banner" style={{ background: verifiedBanner === 'true' ? 'var(--success, #16a34a)' : 'var(--error, #dc2626)' }}>
              <div className="onboarding-banner__body">
                {verifiedBanner === 'true'
                  ? <><strong>Email verified!</strong> <span>Your account is now fully active. Sign in to participate.</span></>
                  : <><strong>Verification failed.</strong> <span>This link has already been used or has expired. Try registering again.</span></>
                }
              </div>
              <button className="onboarding-banner__close" onClick={() => setVerifiedBanner(null)} aria-label="Dismiss">×</button>
            </div>
          )}

          {user && showOnboarding && (
            <div className="onboarding-banner">
              <div className="onboarding-banner__body">
                <strong>Welcome to Proteste</strong>
                <span> — your anonymous ID is <code>{user.user_id}</code>. No one can link it to your identity. Browse topics, share your voice, and help shape the global picture.</span>
              </div>
              <button className="onboarding-banner__close" onClick={dismissOnboarding} aria-label="Dismiss">×</button>
            </div>
          )}

          {(currentPage === 'login' || resetToken) && !user && (
            <AuthPage
              onRegister={handleRegister}
              onLogin={handleLogin}
              onAdminAccess={() => setCurrentPage('admin-login')}
              onBack={() => setCurrentPage('dashboard')}
              initialResetToken={resetToken}
            />
          )}

          {currentPage === 'admin-login' && (
            <div>
              <AdminLoginForm onAdminLogin={handleAdminLogin} />
              <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                <button
                  onClick={() => setCurrentPage('login')}
                  style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.875rem' }}
                >
                  Back to user login
                </button>
              </div>
            </div>
          )}

          {currentPage === 'dashboard' && (
            <DashboardPage
              user={user}
              topics={topics}
              onSelectTopic={user ? handleSelectTopic : () => setCurrentPage('login')}
              onRefreshTopics={fetchTopics}
            />
          )}

          {currentPage === 'topic' && selectedTopic && (
            <TopicPage
              user={user}
              topic={selectedTopic}
              onBack={() => setCurrentPage('dashboard')}
              onSynthesize={fetchTopics}
            />
          )}

          {currentPage === 'api-access' && <ApiAccessPage />}
        </main>
        <Footer />
      </div>
    </ThemeContext.Provider>
  );
}

function AppWithBoundary() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}

export default AppWithBoundary;
